/*
 * Narutieum Monitor
 * Copyright (c) 2026 Narutieum
 * Licensed under GPLv3.
 * License guide: https://narutieum.com/bbs/content.php?co_id=naru_license
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { fetchMonitorGroups, fetchMonitorMainCards, monitorLogout } from "../../api/client";
import CustomMainPage from "./custom/CustomMainPage";
import { resolveMainLayoutSkin } from "./skins";

const THEME_RANGES = {
  clear: {
    "--shell-page-bg": ["#ffffff", "#363b44"],
    "--shell-frame-bg": ["#ffffff", "#2f343c"],
    "--shell-top-bg": ["#ffffff", "#242830"],
    "--shell-top-border": ["#dce6f3", "#535b66"],
    "--shell-session-bg": ["#ffffff", "#333842"],
    "--shell-session-border": ["#aaaaaa", "#737b87"],
    "--shell-content-bg": ["#ffffff", "#3d434d"],
    "--shell-footer-bg": ["#f7faff", "#2f343c"],
    "--shell-footer-border": ["#dce6f3", "#535b66"],
  },
  deep_blue: {
    "--shell-page-bg": ["#eaf2ff", "#07111f"],
    "--shell-frame-bg": ["#f6faff", "#0b1a31"],
    "--shell-top-bg": ["#f9fbff", "#081426"],
    "--shell-top-border": ["#c9dcfa", "#24456f"],
    "--shell-session-bg": ["#ffffff", "#162b4e"],
    "--shell-session-border": ["#b8cdf0", "#3d659b"],
    "--shell-content-bg": ["#edf5ff", "#10233e"],
    "--shell-footer-bg": ["#f6faff", "#0f1f39"],
    "--shell-footer-border": ["#c9dcfa", "#24456f"],
  },
};

function clampPercent(value, fallback = 35) {
  const number = Number.parseInt(value, 10);
  if (Number.isNaN(number)) return fallback;
  return Math.max(0, Math.min(number, 100));
}

function hexToRgb(hex) {
  const value = normalizeHex(hex, "#ffffff").replace("#", "");
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function normalizeHex(value, fallback) {
  const raw = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw.toLowerCase() : fallback;
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mixHex(from, to, amount) {
  const start = hexToRgb(from);
  const end = hexToRgb(to);
  return rgbToHex({
    r: start.r + (end.r - start.r) * amount,
    g: start.g + (end.g - start.g) * amount,
    b: start.b + (end.b - start.b) * amount,
  });
}

function mixThreeHex(left, middle, right, amount) {
  if (amount <= 0.5) {
    return mixHex(left, middle, amount * 2);
  }
  return mixHex(middle, right, (amount - 0.5) * 2);
}

function colorBrightness(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

function buildCustomThemeStyle(mainStyle, strength) {
  const left = normalizeHex(mainStyle.custom_color_left, "#ffffff");
  const middle = normalizeHex(mainStyle.custom_color_middle, "#eef3fb");
  const right = normalizeHex(mainStyle.custom_color_right, "#333333");
  const base = mixThreeHex(left, middle, right, strength);
  const surface = mixHex(base, colorBrightness(base) > 145 ? "#ffffff" : "#111827", 0.34);
  const border = mixHex(base, colorBrightness(base) > 145 ? "#aeb7c4" : "#5f6875", 0.44);
  const darkText = colorBrightness(surface) > 150;
  return {
    "--shell-page-bg": base,
    "--shell-frame-bg": surface,
    "--shell-top-bg": surface,
    "--shell-top-border": border,
    "--shell-session-bg": surface,
    "--shell-session-border": border,
    "--shell-content-bg": base,
    "--shell-footer-bg": surface,
    "--shell-footer-border": border,
    "--shell-brand": darkText ? "#152842" : "#f7fbff",
    "--shell-session-text": darkText ? "#0f1729" : "#f7fbff",
    "--shell-footer-text": darkText ? "#607087" : "#dbe8ff",
    "--shell-icon-bg": darkText ? "#222222" : "#f7fbff",
    "--shell-icon-fg": darkText ? "#ffffff" : "#101826",
  };
}

function buildShellThemeStyle(theme, strengthValue, mainStyle = {}) {
  const strength = clampPercent(strengthValue) / 100;
  if (theme === "custom") {
    return buildCustomThemeStyle(mainStyle, strength);
  }
  const ranges = THEME_RANGES[theme] || THEME_RANGES.clear;
  const style = Object.entries(ranges).reduce((acc, [key, [from, to]]) => {
    acc[key] = mixHex(from, to, strength);
    return acc;
  }, {});

  if (theme === "clear") {
    if (strength < 0.72) {
      style["--shell-frame-bg"] = "#ffffff";
      style["--shell-top-bg"] = "#ffffff";
      style["--shell-footer-bg"] = "#f8fbff";
      style["--shell-top-border"] = mixHex("#dce6f3", "#aeb7c4", Math.min(strength * 1.25, 1));
      style["--shell-footer-border"] = mixHex("#dce6f3", "#aeb7c4", Math.min(strength * 1.25, 1));
    } else {
      style["--shell-frame-bg"] = "#252a32";
      style["--shell-top-bg"] = "#1f242c";
      style["--shell-footer-bg"] = "#252a32";
      style["--shell-top-border"] = "#555e6b";
      style["--shell-footer-border"] = "#555e6b";
    }
  }

  if (theme === "deep_blue") {
    style["--shell-top-bg"] = mixHex("#ffffff", "#06101f", Math.min(strength * 1.12, 1));
    style["--shell-footer-bg"] = mixHex("#f6faff", "#0a172b", Math.min(strength * 1.08, 1));
  }

  const darkText = theme === "clear" ? strength < 0.58 : strength < 0.42;
  style["--shell-brand"] = darkText ? "#152842" : "#f7fbff";
  style["--shell-session-text"] = darkText ? "#0f1729" : "#f7fbff";
  style["--shell-footer-text"] = darkText ? "#607087" : "#dbe8ff";
  style["--shell-icon-bg"] = darkText ? "#222222" : "#f7fbff";
  style["--shell-icon-fg"] = darkText ? "#ffffff" : "#101826";
  return style;
}

export default function MainPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = React.useState([]);
  const [cards, setCards] = React.useState([]);
  const [slots, setSlots] = React.useState([]);
  const [layout, setLayout] = React.useState({ preset: "grid", columns: 2, rows: 2 });
  const [renderMode, setRenderMode] = React.useState("grid");
  const [mainStyle, setMainStyle] = React.useState({});
  const [branding, setBranding] = React.useState({ brand_name: "나루틔움", logo_url: "" });
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let mounted = true;

    Promise.all([fetchMonitorGroups(), fetchMonitorMainCards()])
      .then(([groupData, cardData]) => {
        if (!mounted) return;
        setGroups(groupData.groups || []);
        setCards(cardData.cards || []);
        setSlots(cardData.slots || []);
        setLayout(cardData.layout || { preset: "grid", columns: 2, rows: 2 });
        setRenderMode(cardData.render_mode || "grid");
        setMainStyle(cardData.main_style || {});
        setBranding(cardData.branding || { brand_name: "나루틔움", logo_url: "" });
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError.message || "메인 데이터를 불러오지 못했습니다.");
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = async () => {
    await monitorLogout();
    navigate("/login", { replace: true });
  };

  const SelectedMainLayoutSkin = resolveMainLayoutSkin("basic");
  const shellTheme = mainStyle.theme || "clear";
  const shellThemeStyle = buildShellThemeStyle(shellTheme, mainStyle.theme_strength, mainStyle);
  const backgroundColorValue = mainStyle.background_color || "";
  const isGradientBackground = backgroundColorValue.includes("gradient(");
  const contentStyle = {
    ...(backgroundColorValue && isGradientBackground ? { background: backgroundColorValue } : {}),
    ...(backgroundColorValue && !isGradientBackground ? { backgroundColor: backgroundColorValue } : {}),
    ...(mainStyle.background_image_url ? { backgroundImage: `url("${mainStyle.background_image_url}")` } : {}),
    ...(mainStyle.background_size ? { backgroundSize: mainStyle.background_size } : {}),
    ...(mainStyle.background_position ? { backgroundPosition: mainStyle.background_position } : {}),
    ...(mainStyle.background_image_url ? { backgroundRepeat: "no-repeat" } : {}),
  };
  const mainContent = renderMode === "custom_file" ? (
    <CustomMainPage cards={cards} slots={slots} groups={groups} layout={layout} error={error} />
  ) : (
    <SelectedMainLayoutSkin
      cards={cards}
      slots={slots}
      groups={groups}
      layout={layout}
      error={error}
      links={[
        { to: "/monitor", label: "그룹 목록" },
        { to: "/monitor/devices", label: "디바이스 목록" },
      ]}
    />
  );

  return (
    <AppShell
      theme={shellTheme}
      themeStyle={shellThemeStyle}
      contentStyle={contentStyle}
      brandName={branding.brand_name || "나루틔움"}
      brandLogoUrl={branding.logo_url || ""}
      actions={<button className="action-btn" onClick={handleLogout} title="로그아웃">
        <svg viewBox="-8 -5 40 30" preserveAspectRatio="none" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M2 6.5C2 4.01472 4.01472 2 6.5 2H12C14.2091 2 16 3.79086 16 6V7C16 7.55228 15.5523 8 15 8C14.4477 8 14 7.55228 14 7V6C14 4.89543 13.1046 4 12 4H6.5C5.11929 4 4 5.11929 4 6.5V17.5C4 18.8807 5.11929 20 6.5 20H12C13.1046 20 14 19.1046 14 18V17C14 16.4477 14.4477 16 15 16C15.5523 16 16 16.4477 16 17V18C16 20.2091 14.2091 22 12 22H6.5C4.01472 22 2 19.9853 2 17.5V6.5ZM18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289L22.7071 11.2929C23.0976 11.6834 23.0976 12.3166 22.7071 12.7071L19.7071 15.7071C19.3166 16.0976 18.6834 16.0976 18.2929 15.7071C17.9024 15.3166 17.9024 14.6834 18.2929 14.2929L19.5858 13L11 13C10.4477 13 10 12.5523 10 12C10 11.4477 10.4477 11 11 11L19.5858 11L18.2929 9.70711C17.9024 9.31658 17.9024 8.68342 18.2929 8.29289Z" fill="currentColor"/>
        </svg>
      </button>}
    >
      {mainContent}
    </AppShell>
  );
}
